// API base URL - replace with your actual Worker URL
const API_BASE_URL = 'https://squire-bot.alephium.workers.dev/api';

// Types matching your database structure

/**
 * @typedef {Object} User
 * @property {number} id
 * @property {number} telegram_id
 * @property {string} username
 * @property {number} submission_xp - XP from submissions
 * @property {number} total_xp - Total XP (submission + referral + bonus)
 * @property {number} referral_xp - XP from referral commissions
 * @property {number} bonus_xp - XP from category bonuses
 * @property {number} submissions_count
 * @property {string} created_at
 * @property {string} last_cycle_reset - Last biweekly cycle reset date
 * @property {boolean} banned
 * @property {number} historical_xp - Historical XP total
 * @property {number} historical_submissions_count - Historical submissions count
 * @property {string} last_weekly_reset - Last weekly reset date
 * @property {string} current_week_start - Current week start date
 * @property {number} [applicant_id] - Associated applicant ID (optional)
 */

/**
 * @typedef {Object} Submission
 * @property {number} id
 * @property {number} user_id
 * @property {number} telegram_id
 * @property {string} url
 * @property {'twitter' | 'youtube' | 'comment' | 'retweet'} platform
 * @property {'twitter' | 'youtube' | 'comment' | 'retweet'} category
 * @property {number} base_xp
 * @property {number} quality_multiplier
 * @property {number} manual_boost
 * @property {number} final_xp
 * @property {boolean} processed
 * @property {string} submitted_at
 * @property {'basic' | 'extra'} task_type - Task type classification
 * @property {boolean} is_valid - Validation status
 * @property {number} user_followers - User's follower count at submission time
 * @property {string} week_start_date - Week start date for the submission
 * @property {string | null} comment_target_account - Target account for comments (RETWEET_MARKER for retweets)
 * @property {number} bonus_xp - Additional bonus XP
 * @property {string | null} processed_at - When the submission was processed
 * @property {string} [username] - Username (frontend only)
 * @property {number} [twitter_likes] - Twitter likes count
 * @property {number} [twitter_retweets] - Twitter retweets count
 * @property {number} [twitter_replies] - Twitter replies count
 * @property {number} [twitter_views] - Twitter views count
 * @property {number} [youtube_views] - YouTube views count
 * @property {number} [youtube_likes] - YouTube likes count
 * @property {number} [youtube_comments] - YouTube comments count
 */

/**
 * Category bonus system:
 * - retweet: Bonus based on followers when 3rd retweet is submitted (individual retweet submissions get 0 XP)
 * - comment: 10 XP bonus when 7/7 daily comments are completed  
 * - youtube: 20 XP bonus after 5 youtube video submissions
 * - twitter: Standard per-submission XP (no bonus system)
 * 
 * @typedef {Object} CategoryBonus
 * @property {'twitter' | 'youtube' | 'comment' | 'retweet'} category - The category this bonus applies to
 * @property {number} bonus - The bonus multiplier or additional XP
 * @property {string} description - Description of what this bonus is for
 * @property {string} requirement - What the user needs to do to get this bonus
 * @property {"1" | "2"} week - Which week this bonus applies to
 */

/**
 * @typedef {User & {rank: number, submission_count: number, calculated_total_xp: number, alph_address: string}} LeaderboardEntry
 */

/**
 * @typedef {Object} UserDetailedStats
 * @property {User} user
 * @property {Submission[]} submissions
 * @property {Object} stats
 * @property {number} stats.total_submissions
 * @property {number} stats.twitter_submissions
 * @property {number} stats.comment_submissions
 * @property {number} stats.youtube_submissions
 * @property {number} stats.retweet_submissions
 * @property {number} stats.best_submission_xp
 * @property {CategoryBonus[]} bonuses
 * @property {ApplicantLog[]} [applicant_logs] - Audit logs for applicant changes (optional)
 */

/**
 * @typedef {Object} Applicant
 * @property {number} id
 * @property {number} telegram_id
 * @property {string} telegram_username
 * @property {string} twitter_username
 * @property {string} alph_address
 * @property {string | null} discord_handle
 * @property {string | null} youtube_handle
 * @property {number | null} referrer_code
 * @property {boolean} approved
 * @property {boolean} banned
 * @property {number} [twitter_followers]
 * @property {string} created_at
 * @property {string} submission_time - Time of application submission
 * @property {string | null} note - Admin notes (frontend only)
 */

/**
 * @typedef {Object} Referral
 * @property {number} id
 * @property {number} referrer_id
 * @property {string | null} referrer_name
 * @property {number} referrer_code
 * @property {number} new_user_id
 * @property {string | null} new_user_name
 * @property {number} new_user_cycle_submission_xp - New user's submission XP for current biweekly cycle
 * @property {number} referrer_cycle_commission_xp - Referrer's 10% commission XP for current biweekly cycle
 * @property {string} validity_end_date
 * @property {string} created_at
 * @property {string} updated_at
 */

/**
 * @typedef {Object} ApplicantLog
 * @property {number} id
 * @property {number} applicant_id
 * @property {number} telegram_id
 * @property {string} change_type - Type of change made
 * @property {any} changes - JSON object containing the changes
 * @property {string} changed_at - Timestamp of the change
 */

/**
 * @typedef {Applicant & {referrer_name: string | null, existing_referral_id?: number, validity_end_date?: string, new_user_cycle_submission_xp?: number, referrer_cycle_commission_xp?: number}} ApplicantWithReferrer
 */

/**
 * @typedef {Object} ScoreOverview
 * @property {CategoryXP} twitter_xp
 * @property {CategoryXP} comment_xp
 * @property {CategoryXP} youtube_xp
 * @property {CategoryXP} retweet_xp
 * @property {Object} referral_xp
 * @property {number} referral_xp.total_referrer_xp
 * @property {number} referral_xp.total_referrals
 * @property {number} referral_xp.active_referrals
 * @property {number} grand_total_xp
 * @property {number} total_users
 * @property {number} total_submissions
 * @property {string} generated_at
 */

/**
 * @typedef {Object} CategoryXP
 * @property {number} submission_xp
 * @property {number} bonus_xp
 * @property {number} total_xp
 * @property {number} submission_count
 */

// API service class
class ApiService {
  async fetchWithError(url, options) {
    try {
      const response = await fetch(url, options);
      
      if (!response.ok) {
        // Try to get error message from response body
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorBody = await response.json();
          if (errorBody.error) {
            errorMessage = errorBody.error;
          }
        } catch (e) {
          // If we can't parse the error body, use the default message
        }
        throw new Error(errorMessage);
      }
      
      return await response.json();
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  // Users API
  async getUsers(search, limit = 100, offset = 0) {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    params.append('limit', limit.toString());
    params.append('offset', offset.toString());

    return this.fetchWithError(`${API_BASE_URL}/users?${params}`);
  }

  async getUserDetails(userId) {
    return this.fetchWithError(`${API_BASE_URL}/users/${userId}`);
  }

  async getUserDetailsByUsername(username) {
    return this.fetchWithError(`${API_BASE_URL}/users/username/${username}`);
  }

  // Submissions API
  async getSubmissions(filters = {}) {
    const params = new URLSearchParams();
    
    if (filters.platform) params.append('platform', filters.platform);
    if (filters.processed !== undefined) params.append('processed', filters.processed.toString());
    if (filters.search) params.append('search', filters.search);
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.offset) params.append('offset', filters.offset.toString());

    return this.fetchWithError(`${API_BASE_URL}/submissions?${params}`);
  }

  // Leaderboard API
  async getLeaderboard(timeframe = 'all', limit = 100, offset = 0, xpCategory = 'general') {
    const params = new URLSearchParams();
    params.append('timeframe', timeframe);
    params.append('limit', limit.toString());
    params.append('offset', offset.toString());
    if (xpCategory !== 'general') {
      params.append('xp_category', xpCategory);
    }

    return this.fetchWithError(`${API_BASE_URL}/leaderboard?${params}`);
  }

  // Applicants API
  async getApplicants(limit = 100, offset = 0, search) {
    const params = new URLSearchParams();
    params.append('limit', limit.toString());
    params.append('offset', offset.toString());
    if (search) {
      params.append('search', search);
    }

    return this.fetchWithError(`${API_BASE_URL}/applicants?${params}`);
  }

  async updateApplicant(id, updates) {
    await this.fetchWithError(`${API_BASE_URL}/applicants/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updates),
    });
  }

  async deleteApplicant(id) {
    await this.fetchWithError(`${API_BASE_URL}/applicants/${id}`, {
      method: 'DELETE',
    });
  }

  async getApplicantLogs(applicantId, telegramId, limit = 100, offset = 0) {
    const params = new URLSearchParams();
    if (applicantId) params.append('applicant_id', applicantId.toString());
    if (telegramId) params.append('telegram_id', telegramId.toString());
    params.append('limit', limit.toString());
    params.append('offset', offset.toString());

    return this.fetchWithError(`${API_BASE_URL}/applicant-logs?${params}`);
  }

  async updateManualMultiplier(submissionId, manualMultiplier) {
    await this.fetchWithError(`${API_BASE_URL}/submissions/${submissionId}/manual-multiplier`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ manual_multiplier: manualMultiplier }),
    });
  }

  async deleteSubmission(submissionId) {
    await this.fetchWithError(`${API_BASE_URL}/submissions/${submissionId}`, {
      method: 'DELETE',
    });
  }

  // Referrals API
  async getApplicantsWithReferrers(limit = 100, offset = 0, telegramId, hasSubmissionXp = false) {
    const params = new URLSearchParams();
    params.append('limit', limit.toString());
    params.append('offset', offset.toString());
    if (telegramId) params.append('telegram_id', telegramId.toString());
    if (hasSubmissionXp) params.append('has_submission_xp', hasSubmissionXp.toString());

    return this.fetchWithError(`${API_BASE_URL}/applicants-with-referrers?${params}`);
  }

  async getReferrals(limit = 100, offset = 0, telegramId) {
    const params = new URLSearchParams();
    params.append('limit', limit.toString());
    params.append('offset', offset.toString());
    if (telegramId) {
      params.append('telegram_id', telegramId.toString());
    }

    return this.fetchWithError(`${API_BASE_URL}/referrals?${params}`);
  }

  async updateReferralCommission(applicantId, commissionXP) {
    await this.fetchWithError(`${API_BASE_URL}/referrals/${applicantId}/commission`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ commission_xp: commissionXP }),
    });
  }

  // Category Bonuses API
  async getCategoryBonuses(filters = {}) {
    const params = new URLSearchParams();
    
    if (filters.category) params.append('category', filters.category);
    if (filters.week) params.append('week', filters.week);
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.offset) params.append('offset', filters.offset.toString());

    return this.fetchWithError(`${API_BASE_URL}/category-bonuses?${params}`);
  }

  async createCategoryBonus(bonusData) {
    return this.fetchWithError(`${API_BASE_URL}/category-bonuses`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(bonusData),
    });
  }

  async updateCategoryBonus(bonusId, updates) {
    return this.fetchWithError(`${API_BASE_URL}/category-bonuses/${bonusId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updates),
    });
  }

  async deleteCategoryBonus(bonusId) {
    return this.fetchWithError(`${API_BASE_URL}/category-bonuses/${bonusId}`, {
      method: 'DELETE',
    });
  }

  // Score Overview API
  async getScoreOverview() {
    return this.fetchWithError(`${API_BASE_URL}/score-overview`);
  }

  // Raffle API
  async getActiveRaffle() {
    return this.fetchWithError(`${API_BASE_URL}/public/raffle`);
  }

  async getRaffleWinners(limit = 100, offset = 0) {
    const params = new URLSearchParams();
    params.append('limit', limit.toString());
    params.append('offset', offset.toString());

    return this.fetchWithError(`${API_BASE_URL}/public/raffle/winners?${params}`);
  }

  async getRaffleStats() {
    return this.fetchWithError(`${API_BASE_URL}/public/raffle/stats`);
  }
}

export const apiService = new ApiService();

// Simple test function to check API connection
export const testDatabaseConnection = async () => {
  console.log('🧪 Testing API connection...');
  
  try {
    console.log('🧪 Testing /api/leaderboard endpoint');
    const response = await apiService.getLeaderboard();
    console.log('🧪 Success with /api/leaderboard:', response);
    return true;
  } catch (error) {
    console.log('🧪 Failed /api/leaderboard:', error.message);
    return false;
  }
};

// Function to explore API endpoints
export const exploreAPI = async () => {
  console.log('🔍 Exploring known API endpoints...');
  
  const results = {};
  
  try {
    const users = await apiService.getUsers();
    results['/api/users'] = { status: 200, data: users };
    console.log('✅ /api/users:', 200, users);
  } catch (error) {
    results['/api/users'] = { status: 'ERROR', error: error.message };
    console.log('❌ /api/users:', error.message);
  }

  try {
    const submissions = await apiService.getSubmissions();
    results['/api/submissions'] = { status: 200, data: submissions };
    console.log('✅ /api/submissions:', 200, submissions);
  } catch (error) {
    results['/api/submissions'] = { status: 'ERROR', error: error.message };
    console.log('❌ /api/submissions:', error.message);
  }

  try {
    const leaderboard = await apiService.getLeaderboard();
    results['/api/leaderboard'] = { status: 200, data: leaderboard };
    console.log('✅ /api/leaderboard:', 200, leaderboard);
  } catch (error) {
    results['/api/leaderboard'] = { status: 'ERROR', error: error.message };
    console.log('❌ /api/leaderboard:', error.message);
  }

  try {
    const applicants = await apiService.getApplicants();
    results['/api/applicants'] = { status: 200, data: applicants };
    console.log('✅ /api/applicants:', 200, applicants);
  } catch (error) {
    results['/api/applicants'] = { status: 'ERROR', error: error.message };
    console.log('❌ /api/applicants:', error.message);
  }

  try {
    const referrals = await apiService.getReferrals();
    results['/api/referrals'] = { status: 200, data: referrals };
    console.log('✅ /api/referrals:', 200, referrals);
  } catch (error) {
    results['/api/referrals'] = { status: 'ERROR', error: error.message };
    console.log('❌ /api/referrals:', error.message);
  }
  
  return results;
};

// Fetch category leaderboard using apiService
export const fetchCategoryLeaderboard = async (category) => {
  console.log(`🔄 Fetching ${category} leaderboard...`);
  
  try {
    let response;
    
    // Get submissions for specific categories
    switch (category) {
      case 'yourpost':
        response = await apiService.getSubmissions({ platform: 'twitter', limit: 1000 });
        break;
      case 'comment':
        response = await apiService.getSubmissions({ platform: 'comment', limit: 1000 });
        break;
      case 'youtube':
        response = await apiService.getSubmissions({ platform: 'youtube', limit: 1000 });
        break;
      default:
        response = await apiService.getLeaderboard('all', 1000, 0);
        break;
    }
    
    return processCategoryResponse(response, category);
    
  } catch (error) {
    console.error(`Error fetching ${category} leaderboard:`, error);
    throw new Error(`Failed to fetch ${category} leaderboard: ${error.message}`);
  }
};

// Process API response for category data
const processCategoryResponse = (data, category) => {
  console.log(`📊 Processing ${category} response...`);
  
  let submissions = Array.isArray(data) ? data : data.data || [];
  
  if (!Array.isArray(submissions)) {
    console.warn('⚠️ Expected array but got:', typeof submissions);
    return [];
  }
  
  // Group submissions by user and calculate totals
  const userStats = new Map();
  
  submissions.forEach(submission => {
    const userId = submission.user_id || submission.telegram_id;
    const username = submission.username || submission.user?.username || `User_${userId}`;
    const xp = Number(submission.final_xp || submission.base_xp || 0);
    
    if (!userId) return;
    
    // Filter out banned users
    const userObj = submission.user || submission;
    const isBanned = userObj.banned || userObj.is_banned || userObj.status === 'banned';
    
    if (isBanned) {
      console.log(`🚫 Filtering out banned user from ${category}: ${username}`);
      return;
    }
    
    if (!userStats.has(userId)) {
      userStats.set(userId, {
        id: userId,
        username: username,
        totalXP: 0,
        submissions: 0
      });
    }
    
    const user = userStats.get(userId);
    user.totalXP += xp;
    user.submissions += 1;
  });
  
  // Convert to array and sort
  const result = Array.from(userStats.values())
    .filter(user => user.totalXP > 0)
    .sort((a, b) => b.totalXP - a.totalXP);
    
  console.log(`✅ ${category} leaderboard ready with ${result.length} users`);
  return result;
};

// Load category data using apiService
export const loadCategoryData = async (category) => {
  console.log(`🔄 Loading ${category} category data...`);
  
  try {
    return await fetchCategoryLeaderboard(category);
  } catch (error) {
    console.error(`Error loading ${category} category data:`, error);
    return [];
  }
};

// Fetch leaderboard data using apiService
export const fetchLeaderboardData = async () => {
  console.log('🔄 Fetching leaderboard data...');
  
  try {
    const response = await apiService.getLeaderboard('all', 10000, 0);
    const usersData = response.data || [];
    
    if (!Array.isArray(usersData)) {
      throw new Error(`API returned invalid data format. Expected array, got ${typeof usersData}`);
    }
    
    console.log('👥 Processing', usersData.length, 'users...');
    
    // Process and filter users
    const processedUsers = usersData
      .filter(user => !user.banned)
      .map(user => ({
        id: user.id,
        telegram_id: user.telegram_id,
        username: user.username,
        total_xp: user.total_xp || 0,
        referral_xp: user.referral_xp || 0,
        bonus_xp: user.bonus_xp || 0,
        submissions_count: user.submissions_count || 0,
        banned: user.banned || false,
        totalXP: user.total_xp || 0,
        submissions: user.submissions_count || 0,
        referralXP: user.referral_xp || 0,
        submissionsXP: user.submission_xp || 0,
        bonusXP: user.bonus_xp || 0
      }))
      .sort((a, b) => b.totalXP - a.totalXP);
    
    const processedData = {
      general: processedUsers,
      yourpost: [],
      comment: [],
      youtube: []
    };

    console.log('✅ Leaderboard ready with', processedData.general.length, 'users');
    return processedData;
    
  } catch (error) {
    console.error('Error fetching leaderboard data:', error);
    throw new Error(`Failed to fetch leaderboard data: ${error.message}`);
  }
};

// Search users with ranks using apiService
export const searchUsersWithRanks = async (searchTerm) => {
  try {
    console.log('🔍 Searching for users with term:', searchTerm);
    
    // Get search results and leaderboard in parallel
    const [searchResponse, leaderboardResponse] = await Promise.all([
      apiService.getUsers(searchTerm, 20, 0),
      apiService.getLeaderboard('all', 10000, 0)
    ]);
    
    // Handle search results
    const searchResults = Array.isArray(searchResponse) ? searchResponse : searchResponse.data || [];
    const fullLeaderboard = Array.isArray(leaderboardResponse) ? leaderboardResponse : leaderboardResponse.data || [];
    
    // Create ranking map
    const rankingMap = new Map();
    fullLeaderboard
      .sort((a, b) => Number(b.total_xp || 0) - Number(a.total_xp || 0))
      .forEach((user, index) => {
        const userId = user.id || user.telegram_id;
        rankingMap.set(userId, index + 1);
      });
    
    // Process search results
    const processedResults = searchResults
      .filter(user => !user.banned)
      .map(user => {
        const userId = user.id || user.telegram_id;
        const username = user.username || user.telegram_id || `User_${user.id}`;
        const realRank = rankingMap.get(userId) || 'N/A';
        const totalXP = user.total_xp || 0;
        
        return {
          id: userId,
          username: username,
          totalXP: totalXP,
          submissions: user.submissions_count || 0,
          submissionsXP: user.submission_xp || 0,
          referralXP: user.referral_xp || 0,
          bonusXP: user.bonus_xp || 0,
          realRank: realRank,
          retweet: { xp: 0, submissions: 0 },
          twitter: { xp: 0, submissions: 0 },
          comment: { xp: 0, submissions: 0 },
          youtube: { xp: 0, submissions: 0 }
        };
      })
      .sort((a, b) => {
        if (a.realRank === 'N/A') return 1;
        if (b.realRank === 'N/A') return -1;
        return a.realRank - b.realRank;
      });
    
    console.log('🔍 Processed search results with ranks:', processedResults);
    return processedResults;
    
  } catch (error) {
    console.error('Error searching users:', error);
    throw new Error(`Failed to search users: ${error.message}`);
  }
};

// Keep the old function for backwards compatibility
export const searchUsers = searchUsersWithRanks;

// Get user details using apiService
export const getUserDetails = async (userId) => {
  try {
    console.log('Fetching user details for ID:', userId);
    const response = await apiService.getUserDetails(userId);

    if (!response || !response.user) {
      console.warn('No user data found in response');
      return null;
    }

    const user = response.user;
    const stats = response.stats || {};
    const submissions = response.submissions || [];
    const bonuses = response.bonuses || [];
    
    // Calculate category breakdown from submissions
    let retweetXp = 0, twitterXp = 0, commentXp = 0, youtubeXp = 0;
    let retweetSubs = 0, twitterSubs = 0, commentSubs = 0, youtubeSubs = 0;
    
    submissions.forEach((submission) => {
      const finalXp = Number(submission.final_xp || 0);
      const platform = submission.platform?.toLowerCase();
      const category = submission.category?.toLowerCase();
      
      if (category === 'retweet' || submission.comment_target_account === 'RETWEET_MARKER') {
        retweetXp += finalXp;
        retweetSubs++;
      } else if (platform === 'twitter' || category === 'twitter') {
        twitterXp += finalXp;
        twitterSubs++;
      } else if (platform === 'comment' || category === 'comment') {
        commentXp += finalXp;
        commentSubs++;
      } else if (platform === 'youtube' || category === 'youtube') {
        youtubeXp += finalXp;
        youtubeSubs++;
      }
    });

    return {
      id: user.id,
      telegram_id: user.telegram_id,
      username: user.username,
      submission_xp: user.submission_xp || 0,
      total_xp: user.total_xp || 0,
      referral_xp: user.referral_xp || 0,
      bonus_xp: user.bonus_xp || 0,
      submissions_count: user.submissions_count || 0,
      banned: user.banned || false,
      
      // Legacy compatibility fields
      totalXP: user.total_xp || 0,
      submissions: user.submissions_count || 0,
      submissionsXP: user.submission_xp || 0,
      referralXP: user.referral_xp || 0,
      bonusXP: user.bonus_xp || 0,
      
      // Category breakdown
      retweet: { xp: retweetXp, submissions: stats.retweet_submissions || retweetSubs },
      twitter: { xp: twitterXp, submissions: stats.twitter_submissions || twitterSubs },
      comment: { xp: commentXp, submissions: stats.comment_submissions || commentSubs },
      youtube: { xp: youtubeXp, submissions: stats.youtube_submissions || youtubeSubs },
      
      bonuses: bonuses
    };
  } catch (error) {
    console.error('Error fetching user details:', error);
    throw new Error(`Failed to fetch user details: ${error.message}`);
  }
};